import { useEffect, useRef, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import { DESTINATION_TYPE_ENUM } from "../../../constants/enums";
import { Button, Grid, Tab, Typography } from "@mui/material";
import CustomTabPanel from "./CustomTabPanel";
import RouteItemAccordion from "./RouteItemAccordion";
import { routeActions } from "../../../store/slices/routeSlice";
import LoadingModal from "../../shared/modals/LoadingModal";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import CustomDarkButton from "../../shared/buttons/CustomDarkButton";
import ConfirmationModal from "../../shared/modals/ConfirmationModal";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DayDatePicker from "../../shared/datePickers/DayDatePicker";
import { getDayInWeek } from "../../../helpers/methods/getDay";
import { useNavigate } from "react-router-dom";
import { ORGANIZATION_URL } from "../../../constants/routeConstants";
import { routeGroupActions } from "../../../store/slices/routeGroupSlice";
import RouteGroupCard from "./RouteGroupCard";
import { ModeEditOutlineOutlined } from "@mui/icons-material";
import { TouchSensor, MouseSensor } from '@dnd-kit/core';


// @dnd-kit imports
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCorners,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const SortableRouteItem = ({ id, children, isGroup = false, routeGroupId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    data: {
      type: isGroup ? 'group' : 'route',
      routeGroupId: routeGroupId
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: "8px",
    marginTop: "8px",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const SchedulingTabs = ({ date, setDate, value, setValue }) => {
  const [isReordered, setIsReordered] = useState({
    firstWay: false,
    secondWay: false,
  });
  const { destinations, destinationsByDate } = useSelector(
    (state) => state.destination
  );
  const { routeGroups } = useSelector((state) => state.routeGroup);
  const {
    routesFirstWay,
    routesSecondWay,
    routesLoading,
    updateRouteOrderLoading,
    filter,
  } = useSelector((state) => state.route);
  const [saveReorderOpen, setSaveReorderOpen] = useState({
    firstWay: false,
    secondWay: false,
  });
  const [resetReorderOpen, setResetReorderOpen] = useState({
    firstWay: false,
    secondWay: false,
  });
  const [firstWayMarkersCount, setFirstWayMarkersCount] = useState([]);
  const [secondWayMarkersCount, setSecondWayMarkersCount] = useState([]);
  const [activeId, setActiveId] = useState(null);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // // @dnd-kit sensors
  // const sensors = useSensors(
  //   useSensor(PointerSensor, {
  //     activationConstraint: {
  //       distance: 8,
  //     },
  //   })
  // );
const sensors = useSensors(
  useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  }),
  useSensor(TouchSensor, {
    activationConstraint: {
      delay: 150,  // milisekundi držanja prsta pre nego što počne drag
      tolerance: 5, // koliko piksela može da se pomeri pre drag-a
    },
  })
);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    routeActions.setActiveTab(value);
  }, [value]);

  useEffect(() => {
    dispatch(routeGroupActions.getRouteGroupsFetch({ date }));
  }, [dispatch, date]);

  const refetchRoutes = () => {
    if (destinationsByDate && destinationsByDate.length > 0) {
      const destTypeId = destinationsByDate.filter(
        (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
      )[value].id;

      dispatch(
        routeActions.getRoutesByDestinationTypeFetch({
          date: date,
          search: filter.type === 1 ? filter.search : "",
          destinationTypeId: destTypeId,
        })
      );
    }
  };

  useEffect(() => {
    if (destinationsByDate && destinationsByDate.length > 0) {
      const destTypeId = destinationsByDate.filter(
        (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
      )[value].id;

      dispatch(
        routeActions.getRoutesByDestinationTypeFetch({
          date: date,
          search: filter.type === 1 ? filter.search : "",
          destinationTypeId: destTypeId,
        })
      );
    }
  }, [dispatch, value, destinationsByDate, date, filter]);

  useEffect(() => {
    const calculateAboveMarkers = () => {
      const processRoutes = (routes) => {
        let counters = [];
        let counter = 0;
        let hasNotificationCount = 0;
        let hasQuestionmarkCount = 0;
        let isInactiveCount = 0;

        for (let i = 0; i < routes.length; i++) {
          const route = routes[i];

          if (route.routeGroupId && route.routeGroupId > 0) continue;
          
          if (route.routeId === null) {
            counters.push({
              id: route.id,
              count: counter,
              hasNotificationCount,
              hasQuestionmarkCount,
              isInactiveCount,
            });

            counter = 0;
            hasNotificationCount = 0;
            hasQuestionmarkCount = 0;
            isInactiveCount = 0;
          } else {
            counter += route.passengers?.length || 0;
            if (route.hasNotification) hasNotificationCount++;
            if (route.hasQuestionmark) hasQuestionmarkCount++;
            if (route.isInactive) isInactiveCount++;
          }
        }

        return counters;
      };

      const firstWayCountersResult = processRoutes(routesFirstWay);
      const secondWayCountersResult = processRoutes(routesSecondWay);

      setFirstWayMarkersCount(firstWayCountersResult);
      setSecondWayMarkersCount(secondWayCountersResult);
    };

    calculateAboveMarkers();
  }, [routesFirstWay, routesSecondWay]);

  // Pomoćna funkcija da nađe rutu po ID-u
  const findRouteById = (routes, id) => {
    return routes.find(r => r.id === id);
  };

  // Pomoćna funkcija da proveri da li je ID grupa
  const isGroupId = (id) => {
    return typeof id === 'string' && id.startsWith('group-');
  };

  // Pomoćna funkcija da ekstraktuje pravi ID iz group ID-a
  const extractGroupId = (id) => {
    if (isGroupId(id)) {
      return parseInt(id.replace('group-', ''));
    }
    return id;
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const activeIsGroup = isGroupId(active.id);
    const overIsGroup = isGroupId(over.id);

    // Ne dozvoljavaj drop grupe na grupu
    if (activeIsGroup && overIsGroup) return;

    // nemoj dozvolit marker unutar grupe
   
    
  };

  const handleDragEnd = (event, way) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const routes = way === false ? [...routesFirstWay] : [...routesSecondWay];
    const allRoutes = [...routesFirstWay, ...routesSecondWay];
    
    const activeIsGroup = isGroupId(active.id);
    const overIsGroup = isGroupId(over.id);

    const activeRouteId = activeIsGroup ? null : active.id;
    const overRouteId = overIsGroup ? null : over.id;

    const activeRoute = activeRouteId ? findRouteById(allRoutes, activeRouteId) : null;
    const overRoute = overRouteId ? findRouteById(allRoutes, overRouteId) : null;

    // SCENARIO 1: Prevlačenje rute NA GRUPU (dodaj u grupu)
    if (!activeIsGroup && overIsGroup) {
      const overGroupId = extractGroupId(over.id);
      if (activeRoute?.isMarker) return;
      
      // Ako ruta već pripada ovoj grupi, ne radi ništa
      if (activeRoute && activeRoute.routeGroupId === overGroupId) return;

      // Dodaj rutu u grupu
      dispatch(
        routeGroupActions.addRouteToRouteGroupPut({
          routeId: activeRouteId,
          id: overGroupId,
          onSucceededAddedRouteToRouteGroup,
        })
      );
      return;
    }

    // SCENARIO 2: Prevlačenje rute NA RUTU unutar iste grupe (reorder unutar grupe)
    if (!activeIsGroup && !overIsGroup && activeRoute && overRoute) {
      // Proveri da li obe rute pripadaju istoj grupi
      if (activeRoute.routeGroupId && activeRoute.routeGroupId === overRoute.routeGroupId) {
        const filteredRoutes = routes.filter(
          (x) => x.isAnotherWay === way && x.routeGroupId === activeRoute.routeGroupId
        );

        const oldIndex = filteredRoutes.findIndex((r) => r.id === activeRouteId);
        const newIndex = filteredRoutes.findIndex((r) => r.id === overRouteId);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedRoutes = arrayMove(filteredRoutes, oldIndex, newIndex);
          
          // Rekonstruiši kompletan niz sa izmenjenim rutama
          const newRoutes = routes.map(route => {
            if (route.isAnotherWay === way && route.routeGroupId === activeRoute.routeGroupId) {
              const index = filteredRoutes.findIndex(r => r.id === route.id);
              return reorderedRoutes[index];
            }
            return route;
          });

          dispatch(routeActions.reorderRoutes({ tmpArr: newRoutes, way }));
          setIsReordered((oldState) =>
            way === false
              ? { ...oldState, firstWay: true }
              : { ...oldState, secondWay: true }
          );
        }
        return;
      }

      // SCENARIO 3: Prevlačenje rute iz grupe NA RUTU van grupe (izvuci iz grupe)
      if (activeRoute.routeGroupId && !overRoute.routeGroupId) {
        dispatch(
          routeGroupActions.deleteRouteFromRouteGroupPut({
            routeId: activeRouteId,
            id: activeRoute.routeGroupId,
            onSucceededDeletedRouteFromRouteGroup,
          })
        );
        return;
      }

      // SCENARIO 4: Reorder van grupa (obe rute su van grupa)
      if (!activeRoute.routeGroupId && !overRoute.routeGroupId) {
        const filteredRoutes = routes.filter((x) => x.isAnotherWay === way);
        
        const oldIndex = filteredRoutes.findIndex((r) => r.id === activeRouteId);
        const newIndex = filteredRoutes.findIndex((r) => r.id === overRouteId);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedRoutes = arrayMove(filteredRoutes, oldIndex, newIndex);
          
          const newRoutes = routes.map(route => {
            if (route.isAnotherWay === way) {
              const index = filteredRoutes.findIndex(r => r.id === route.id);
              return reorderedRoutes[index] || route;
            }
            return route;
          });

          dispatch(routeActions.reorderRoutes({ tmpArr: newRoutes, way }));
          setIsReordered((oldState) =>
            way === false
              ? { ...oldState, firstWay: true }
              : { ...oldState, secondWay: true }
          );
        }
        return;
      }
    }

    // SCENARIO 5: Prevlačenje grupa (reorder grupa i pojedinačnih ruta van grupa)
    if (activeIsGroup || overIsGroup) {
      const filteredRoutes = routes.filter((x) => x.isAnotherWay === way);
      
      const grouped = filteredRoutes.reduce((acc, route) => {
        const key = route.routeGroupId || `single-${route.id}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(route);
        return acc;
      }, {});

      const entries = Object.entries(grouped).map(([key, groupRoutes]) => {
        const firstRoute = groupRoutes[0];
        return {
          key,
          groupRoutes,
          firstRoute,
          sortableId: firstRoute.routeGroupId 
            ? `group-${firstRoute.routeGroupId}`
            : firstRoute.id
        };
      });

      const oldIndex = entries.findIndex(e => e.sortableId === active.id);
      const newIndex = entries.findIndex(e => e.sortableId === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedEntries = arrayMove(entries, oldIndex, newIndex);
        
        const newRoutes = reorderedEntries.flatMap(entry => entry.groupRoutes);
        
        const finalRoutes = routes.map(route => {
          if (route.isAnotherWay === way) {
            const newRoute = newRoutes.find(r => r.id === route.id);
            return newRoute || route;
          }
          return route;
        });

        dispatch(routeActions.reorderRoutes({ tmpArr: finalRoutes, way }));
        setIsReordered((oldState) =>
          way === false
            ? { ...oldState, firstWay: true }
            : { ...oldState, secondWay: true }
        );
      }
    }
  };

  if (routesLoading || updateRouteOrderLoading) {
    return <LoadingModal />;
  }

  const onAcceptConfirmSave = (isAnotherWayFlag) => {
    let arr = [];
    arr = !isAnotherWayFlag ? [...routesFirstWay] : [...routesSecondWay];

    const updateOrdersValues = arr.map((route, index) => ({
      id: route.id,
      index,
    }));

    const destTypeId = destinations.filter(
      (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
    )[value].id;

    dispatch(
      routeActions.updateRouteOrderPut({
        date,
        updateOrdersValues,
        routeType: destTypeId,
      })
    );

    if (isAnotherWayFlag === false) {
      setSaveReorderOpen((oldVal) => ({ ...oldVal, firstWay: false }));
      setIsReordered((oldVal) => ({ ...oldVal, firstWay: false }));
    } else {
      setSaveReorderOpen((oldVal) => ({ ...oldVal, secondWay: false }));
      setIsReordered((oldVal) => ({ ...oldVal, secondWay: false }));
    }
  };

  const onResetReorderedRoutes = (isAnotherWayFlag) => {
    if (!isAnotherWayFlag) {
      setResetReorderOpen((oldVal) => ({ ...oldVal, firstWay: false }));
      setIsReordered((oldVal) => ({ ...oldVal, firstWay: false }));
      dispatch(routeActions.resetReorderedRoutes({ way: false }));
    } else {
      setResetReorderOpen((oldVal) => ({ ...oldVal, secondWay: false }));
      setIsReordered((oldVal) => ({ ...oldVal, secondWay: false }));
      dispatch(routeActions.resetReorderedRoutes({ way: true }));
    }
  };

  const onCloseSaveReorderModal = (isAnotherWayFlag) => {
    if (!isAnotherWayFlag) {
      setSaveReorderOpen((oldState) => ({ ...oldState, firstWay: false }));
    } else {
      setSaveReorderOpen((oldState) => ({ ...oldState, secondWay: false }));
    }
  };

  const onCloseResetReorderModal = (isAnotherWayFlag) => {
    if (!isAnotherWayFlag) {
      setResetReorderOpen((oldVal) => ({ ...oldVal, firstWay: false }));
    } else {
      setResetReorderOpen((oldVal) => ({ ...oldVal, secondWay: false }));
    }
  };

  const addMarkerHandler = (isAnotherWayFlag) => {
    const destTypeId = destinationsByDate.filter(
      (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
    )[value].id;
    dispatch(
      routeActions.createMarker({
        date,
        isAnotherWay: isAnotherWayFlag,
        isMarker: true,
        order: 99999,
        destinationId: destTypeId,
        onAddedMarkerSuccessfully,
      })
    );
  };

  const onAddedMarkerSuccessfully = () => {
    const destTypeId = destinationsByDate.filter(
      (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
    )[value].id;

    dispatch(
      routeActions.getRoutesByDestinationTypeFetch({
        date: date,
        search: filter.type === 1 ? filter.search : "",
        destinationTypeId: destTypeId,
      })
    );
  };

  const onDeletedMarkerSuccessfully = () => {
    const destTypeId = destinationsByDate.filter(
      (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
    )[value].id;

    dispatch(
      routeActions.getRoutesByDestinationTypeFetch({
        date: date,
        search: filter.type === 1 ? filter.search : "",
        destinationTypeId: destTypeId,
      })
    );
  };

  const organizationHandler = () => {
    navigate(`${ORGANIZATION_URL}?date=${date.toISOString().split("T")[0]}`);
  };

  const onSucceededAddedRouteToRouteGroup = () => {
    dispatch(routeGroupActions.getRouteGroupsFetch({ date }));
    refetchRoutes();
  };

  const onSucceededDeletedRouteFromRouteGroup = () => {
    dispatch(routeGroupActions.getRouteGroupsFetch({ date }));
    refetchRoutes();
  };

  const renderRoutes = (routes, way, markersCount) => {
    const filteredRoutes = routes.filter((x) => x.isAnotherWay === way);
    
    const grouped = filteredRoutes.reduce((acc, route) => {
      const key = route.routeGroupId || `single-${route.id}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(route);
      return acc;
    }, {});

    const entries = Object.entries(grouped).map(([key, groupRoutes]) => {
      const firstRoute = groupRoutes[0];
      return {
        key,
        groupRoutes,
        firstRoute,
      };
    });

    const sortableIds = entries.map(entry => 
      entry.firstRoute.routeGroupId 
        ? `group-${entry.firstRoute.routeGroupId}`
        : entry.firstRoute.id
    );

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={(e) => handleDragEnd(e, way)}
        onDragOver={handleDragOver}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div style={{ padding: "0 12px" }}>
            {entries.map((entry) => {
              const { key, groupRoutes, firstRoute } = entry;
              const sortableId = firstRoute.routeGroupId 
                ? `group-${firstRoute.routeGroupId}`
                : firstRoute.id;

              return (
                <SortableRouteItem
                  key={key}
                  id={sortableId}
                  isGroup={!!firstRoute.routeGroupId}
                  routeGroupId={firstRoute.routeGroupId}
                >
                  {firstRoute.routeGroupId ? (
                    <RouteGroupCard
                      routeGroupId={firstRoute.routeGroup.id}
                      color={firstRoute.routeGroup.color}
                      calculatedPassengers={groupRoutes.flatMap(
                        (r) => r.passengers || []
                      )}
                      title={firstRoute.routeGroup.title}
                      refetch={onSucceededDeletedRouteFromRouteGroup}
                    >
                      <>
                        {/* Unutar grupe: sortable rute */}
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragStart={handleDragStart}
                          onDragEnd={(e) => handleDragEnd(e, way)}
                        >
                          <SortableContext 
                            items={groupRoutes.map(r => r.id)} 
                            strategy={verticalListSortingStrategy}
                          >
                            {groupRoutes.map((route) => (
                              <SortableRouteItem
                                key={route.id}
                                id={route.id}
                                isGroup={false}
                                routeGroupId={route.routeGroupId}
                              >
                                <RouteItemAccordion
                                  id={route.id}
                                  routeId={route.routeId}
                                  numberOfAdultPassengers={route.numberOfAdultPassengers}
                                  numberOfChildPassengers={route.numberOfChildPassengers}
                                  refetch={onSucceededDeletedRouteFromRouteGroup}
                                  color={route.routeGroup?.color}
                                  preDepartureTime={route.preDepartureTime}
                                  departureTime={route.departureTime}
                                  postDepartureTime={route.postDepartureTime}
                                  passengerNames={route.passengerNames}
                                  address={route.address}
                                  flight={route.flight}
                                  phoneNumber={route.phoneNumber}
                                  contactType={route.contactType}
                                  passengers={route.passengers}
                                  note={route.note}
                                  isMarker={route.isMarker}
                                  hasLuggage={route.hasLuggage}
                                  hasNotification={route.hasNotification}
                                  hasQuestionmark={route.hasQuestionmark}
                                  greenPhone={route.greenPhone}
                                  hasSeats={route.hasSeats}
                                  isInactive={route.isInactive}
                                  wholeCar={route.wholeCar}
                                  wholeBus={route.wholeBus}
                                  sendPhoneNumber={route.sendPhoneNumber}
                                  travelType={route.travelType}
                                  flightTime={route.flightTime}
                                  addressAddress={route.addressAddress}
                                  markersCount={markersCount}
                                  driverNote={route.driverNote}
                                  preDeparture={route.preDeparture}
                                  postDeparture={route.postDeparture}
                                  isSent={route.isSent}
                                  routeGroupId={route.routeGroupId}
                                  onDeletedMarkerSuccessfully={onDeletedMarkerSuccessfully}
                                />
                              </SortableRouteItem>
                            ))}
                          </SortableContext>
                        </DndContext>
                      </>
                    </RouteGroupCard>
                  ) : (
                    <RouteItemAccordion
                      id={firstRoute.id}
                      routeId={firstRoute.routeId}
                      numberOfAdultPassengers={firstRoute.numberOfAdultPassengers}
                      numberOfChildPassengers={firstRoute.numberOfChildPassengers}
                      color={firstRoute.routeGroup?.color}
                      preDepartureTime={firstRoute.preDepartureTime}
                      departureTime={firstRoute.departureTime}
                      postDepartureTime={firstRoute.postDepartureTime}
                      passengerNames={firstRoute.passengerNames}
                      address={firstRoute.address}
                      flight={firstRoute.flight}
                      phoneNumber={firstRoute.phoneNumber}
                      contactType={firstRoute.contactType}
                      passengers={firstRoute.passengers}
                      note={firstRoute.note}
                      isMarker={firstRoute.isMarker}
                      hasLuggage={firstRoute.hasLuggage}
                      hasNotification={firstRoute.hasNotification}
                      hasQuestionmark={firstRoute.hasQuestionmark}
                      greenPhone={firstRoute.greenPhone}
                      hasSeats={firstRoute.hasSeats}
                      isInactive={firstRoute.isInactive}
                      wholeCar={firstRoute.wholeCar}
                      wholeBus={firstRoute.wholeBus}
                      sendPhoneNumber={firstRoute.sendPhoneNumber}
                      travelType={firstRoute.travelType}
                      flightTime={firstRoute.flightTime}
                      addressAddress={firstRoute.addressAddress}
                      markersCount={markersCount}
                      driverNote={firstRoute.driverNote}
                      preDeparture={firstRoute.preDeparture}
                      postDeparture={firstRoute.postDeparture}
                      isSent={firstRoute.isSent}
                      routeGroupId={firstRoute.routeGroupId}
                      onDeletedMarkerSuccessfully={onDeletedMarkerSuccessfully}
                    />
                  )}
                </SortableRouteItem>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <ConfirmationModal
        title="Želite da zapamtite reorganizaciju?"
        open={saveReorderOpen.firstWay}
        onAccept={() => onAcceptConfirmSave(false)}
        onClose={() => onCloseSaveReorderModal(false)}
      />
      <ConfirmationModal
        title="Želite da zapamtite reorganizaciju?"
        open={saveReorderOpen.secondWay}
        onAccept={() => onAcceptConfirmSave(true)}
        onClose={() => onCloseSaveReorderModal(true)}
      />
      <ConfirmationModal
        title="Želite da restartujete reorganizaciju?"
        open={resetReorderOpen.firstWay}
        onAccept={() => onResetReorderedRoutes(false)}
        onClose={() => onCloseResetReorderModal(false)}
      />
      <ConfirmationModal
        title="Želite da restartujete reorganizaciju?"
        open={resetReorderOpen.secondWay}
        onAccept={() => onResetReorderedRoutes(true)}
        onClose={() => onCloseResetReorderModal(true)}
      />
      
      <Box className="header-container">
        <Box>
          <Typography className="header-title">
            POLASCI {getDayInWeek(date.day()).toUpperCase()}
          </Typography>
        </Box>

        <Box className="header-controls">
          <Box className="row-container">
            <Typography
              className="arrow-button"
              onClick={() => setDate((oldVal) => oldVal.subtract(1, "day"))}
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </Typography>

            <DayDatePicker label="" day={date} setDay={setDate} style={{ paddingTop: 0 }} />
            
            <Typography
              className="arrow-button"
              onClick={() => setDate((oldVal) => oldVal.add(1, "day"))}
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CustomDarkButton title="Organizacija" handleClick={organizationHandler} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "white",
          boxShadow: "0 2px 2px rgba(161, 135, 135, 0.5)",
          borderRadius: "8px",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="scrollable auto tabs example"
          TabIndicatorProps={{ style: { backgroundColor: "black" } }}
        >
          {destinationsByDate &&
            destinationsByDate.length > 0 &&
            destinationsByDate.map((destination, index) => (
              <Tab
                key={index}
                label={`${destination.name}`}
                sx={{
                  fontSize: "18px",
                  color: "gray",
                  "&.Mui-selected": { color: "black" },
                }}
                {...a11yProps(index)}
              />
            ))}
        </Tabs>
      </Box>

      {destinationsByDate &&
        destinationsByDate.length > 0 &&
        destinationsByDate.map((_, index) => (
          <CustomTabPanel
            key={index}
            value={value}
            index={index}
            style={{ margin: "12px 0" }}
          >
            <Grid container spacing={2}>
              <Grid item lg={6} md={6} xs={12}>
                <div className="custom-tab-panel-item">
                  <div className="custom-tab-panel-item-header" style={{ padding: "24px" }}>
                    <p style={{ color: "black", fontSize: "20px" }}>
                      Novi Pazar - {destinationsByDate[index].name}
                    </p>
                    {isReordered.firstWay ? (
                      <div className="full-display-flex">
                        <CustomDarkButton
                          handleClick={() => onAcceptConfirmSave(false)}
                          title={<CheckIcon />}
                          className="w-fit-content ms-2"
                        />
                        <CustomDarkButton
                          handleClick={() => onResetReorderedRoutes(false)}
                          title={<ClearIcon />}
                          className="w-fit-content ms-2"
                        />
                      </div>
                    ) : (
                      <Button onClick={() => addMarkerHandler(false)} sx={{ color: "black" }}>
                        <ModeEditOutlineOutlined color="black" />
                      </Button>
                    )}
                  </div>
                  <div>
                    {routesFirstWay && renderRoutes(routesFirstWay, false, firstWayMarkersCount)}
                  </div>
                </div>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <div className="custom-tab-panel-item">
                  <div className="custom-tab-panel-item-header" style={{ padding: "24px" }}>
                    <p style={{ fontSize: "20px", color: "black" }}>
                      {destinationsByDate[index].name} - Novi Pazar
                    </p>
                    {isReordered.secondWay ? (
                      <div className="full-display-flex">
                        <CustomDarkButton
                          handleClick={() => onAcceptConfirmSave(true)}
                          title={<CheckIcon />}
                          className="w-fit-content ms-2"
                        />
                        <CustomDarkButton
                          handleClick={() => onResetReorderedRoutes(true)}
                          title={<ClearIcon />}
                          className="w-fit-content ms-2"
                        />
                      </div>
                    ) : (
                      <Button onClick={() => addMarkerHandler(true)} sx={{ color: "black" }}>
                        <ModeEditOutlineOutlined color="black" />
                      </Button>
                    )}
                  </div>
                  <div>
                    {routesSecondWay && renderRoutes(routesSecondWay, true, secondWayMarkersCount)}
                  </div>
                </div>
              </Grid>
            </Grid>
          </CustomTabPanel>
        ))}
    </Box>
  );
};

export default SchedulingTabs;